import React, { useEffect, useState } from "react";
import { Clock, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Live deadline countdown — ticks every 60 seconds.
 * Color: red if T<24h, amber if T<7d, gray otherwise. Past-due in deep red.
 */
export default function DeadlineCountdown({ dueAt, compact = false }) {
  const { t, i18n } = useTranslation("alerts");
  const [, force] = useState(0);

  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);

  if (!dueAt) {
    return (
      <span className="text-[11px] text-zinc-400 inline-flex items-center gap-1" data-testid="countdown-none">
        <Clock size={11} /> {t("countdown.no_due_date")}
      </span>
    );
  }

  const due = new Date(dueAt);
  if (isNaN(due)) return null;
  const now = new Date();
  const diffMs = due - now;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMin = Math.floor(diffMs / (1000 * 60));
  const expired = diffMs < 0;

  let label;
  let tone = "text-zinc-600";
  let Icon = Clock;

  // Use Intl.RelativeTimeFormat for natural locale phrasing
  const tag = i18n.language?.split("-")[0] === "fr" ? "fr-FR" : "en-US";
  let rtf;
  try {
    rtf = new Intl.RelativeTimeFormat(tag, { numeric: "auto" });
  } catch {
    rtf = null;
  }

  if (expired) {
    const absDays = Math.abs(diffDays);
    label = rtf ? rtf.format(diffDays, "day") : `${absDays}d overdue`;
    tone = "text-rose-700 bg-rose-50 border-rose-200";
    Icon = AlertCircle;
  } else if (diffHours < 24) {
    if (diffMin < 60) {
      label = rtf ? rtf.format(diffMin, "minute") : `in ${diffMin}m`;
    } else {
      label = rtf ? rtf.format(diffHours, "hour") : `in ${diffHours}h`;
    }
    tone = "text-rose-700 bg-rose-50 border-rose-200";
  } else if (diffDays < 7) {
    label = rtf ? rtf.format(diffDays, "day") : `in ${diffDays}d`;
    tone = "text-amber-800 bg-amber-50 border-amber-200";
  } else {
    label = rtf ? rtf.format(diffDays, "day") : `in ${diffDays}d`;
    tone = "text-zinc-700 bg-zinc-50 border-zinc-200";
  }

  if (compact) {
    return (
      <span className={`text-[11px] tabular-nums ${tone.split(" ")[0]}`} data-testid="countdown-compact">
        {label}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium tabular-nums ${tone}`}
      data-testid="countdown"
    >
      <Icon size={11} strokeWidth={2} />
      {label}
    </span>
  );
}
